import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, formatDate, useAuth } from '@halo/web-lib';
import {
  Shield,
  ShieldCheck,
  Loader2,
  Search,
  X,
  Users,
  UserPlus,
  Clock,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Filter,
  Trash2,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';
import { DeleteUserModal } from '../components/DeleteUserModal';

// ─── Types ──────────────────────────────────────

interface UserItem {
  id: string;
  email: string;
  displayName: string | null;
  role: number;
  createdAt: string;
}

const SORT_OPTIONS = ['Email', 'Joined', 'Name'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

const FILTER_OPTIONS = ['All', 'Admins', 'Regular', 'Recent'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

// ─── Helpers ────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function isRecent(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;
}

function userInitials(email: string, name: string | null): string {
  if (name) {
    const parts = name.split(' ');
    return parts.length > 1
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400',
  'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400',
  'bg-teal-100 text-teal-700',
  'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400',
  'bg-emerald-100 text-emerald-700',
  'bg-orange-100 text-orange-700',
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

// ─── Toast ──────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

// ─── Main Component ─────────────────────────────

export function UsersPage() {
  const queryClient = useQueryClient();
  const { toast, showToast } = useToast();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('All');
  const [sortBy, setSortBy] = useState<SortOption>('Email');
  const [confirmDemote, setConfirmDemote] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);
  const { user: currentUser } = useAuth();

  const { data: rawUsers, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async ({ signal }) => {
      const res = await api.get<UserItem[]>('/api/v1/admin/users', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load users');
      return res.data;
    },
  });

  const users = rawUsers ?? [];

  const roleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: number }) => {
      const res = await api.put(`/api/v1/admin/users/${userId}/role`, { role });
      if (!res.success) throw new Error(res.error ?? 'Failed to update role');
      return { userId, role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      const action = data.role === 0 ? 'promoted to admin' : 'demoted to user';
      showToast({ message: `User ${action}`, type: 'success' });
      setConfirmDemote(null);
    },
    onError: (err) => {
      showToast({ message: err instanceof Error ? err.message : 'Failed', type: 'error' });
    },
  });

  // ─── Summary ──────────────────────────────────

  const summary = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role === 0).length,
    regular: users.filter((u) => u.role !== 0).length,
    recent: users.filter((u) => isRecent(u.createdAt)).length,
  }), [users]);

  // ─── Filtered + sorted ────────────────────────

  const filteredUsers = useMemo(() => {
    let list = [...users];

    if (filter === 'Admins') list = list.filter((u) => u.role === 0);
    else if (filter === 'Regular') list = list.filter((u) => u.role !== 0);
    else if (filter === 'Recent') list = list.filter((u) => isRecent(u.createdAt));

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.displayName?.toLowerCase().includes(q) ?? false),
      );
    }

    if (sortBy === 'Joined') list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    else if (sortBy === 'Name') list.sort((a, b) => (a.displayName ?? a.email).localeCompare(b.displayName ?? b.email));
    else list.sort((a, b) => a.email.localeCompare(b.email));

    return list;
  }, [users, filter, search, sortBy]);

  const handleCopyEmail = useCallback((email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 2000);
  }, []);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.message}
          <button onClick={() => showToast(null)} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Users</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {summary.total} total &middot; {summary.admins} admin{summary.admins !== 1 ? 's' : ''}
            {summary.recent > 0 && <span className="text-blue-600 dark:text-blue-400"> &middot; {summary.recent} joined this week</span>}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {([
          { label: 'Total', value: summary.total, color: 'text-gray-900 dark:text-gray-100', icon: Users, filterTo: 'All' as const },
          { label: 'Admins', value: summary.admins, color: 'text-amber-600', icon: ShieldCheck, filterTo: 'Admins' as const },
          { label: 'Regular', value: summary.regular, color: 'text-gray-600 dark:text-gray-300', icon: Shield, filterTo: 'Regular' as const },
          { label: 'Recent (7d)', value: summary.recent, color: summary.recent > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500', icon: UserPlus, filterTo: 'Recent' as const },
        ]).map((card) => (
          <button
            key={card.label}
            onClick={() => setFilter(filter === card.filterTo && filter !== 'All' ? 'All' : card.filterTo)}
            className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
              filter === card.filterTo && filter !== 'All' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <card.icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</span>
            </div>
            <div className={`text-lg font-semibold ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      {/* Search + Filter + Sort */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                filter === opt ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="text-xs border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>Sort: {opt}</option>
          ))}
        </select>
      </div>

      {/* User List */}
      {isLoading ? (
        <Skeleton count={5} />
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {search ? `No users matching "${search}"` : 'No users found'}
          </p>
          {(search || filter !== 'All') && (
            <button onClick={() => { setSearch(''); setFilter('All'); }} className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline">Clear filters</button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 max-h-[600px] overflow-auto">
          {filteredUsers.map((u) => {
            const isAdmin = u.role === 0;
            const initials = userInitials(u.email, u.displayName);
            const color = isAdmin ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300' : avatarColor(u.id);
            const recent = isRecent(u.createdAt);
            const isPending = roleMutation.isPending && roleMutation.variables?.userId === u.id;

            return (
              <Link
                key={u.id}
                to={`/users/${u.id}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${color}`}>
                    {isAdmin ? <ShieldCheck className="w-4 h-4" /> : initials}
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {/* Email with copy */}
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCopyEmail(u.email); }}
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1 truncate"
                        title="Click to copy email"
                      >
                        {u.email}
                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                      </button>
                      {copiedEmail === u.email && (
                        <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">Copied!</span>
                      )}

                      {/* Role badge */}
                      {isAdmin && (
                        <InfoBadge tip="This user has admin privileges and can access the admin panel, manage other users, and configure all stores.">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50 font-medium">
                            Admin
                          </span>
                        </InfoBadge>
                      )}
                      {recent && (
                        <InfoBadge tip="This user created their account within the last 7 days.">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/50 font-medium">
                            New
                          </span>
                        </InfoBadge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {u.displayName && <span className="text-gray-500 dark:text-gray-400">{u.displayName}</span>}
                      <span title={formatDate(u.createdAt)}>
                        <Clock className="w-3 h-3 inline mr-0.5" />
                        {timeAgo(u.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action button */}
                <div className="shrink-0 flex items-center gap-1.5">
                  {isAdmin ? (
                    confirmDemote === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-red-600 dark:text-red-400">Demote?</span>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); roleMutation.mutate({ userId: u.id, role: 1 }); }}
                          disabled={isPending}
                          className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDemote(null); }}
                          className="text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDemote(u.id); }}
                        className="text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      >
                        Demote
                      </button>
                    )
                  ) : (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); roleMutation.mutate({ userId: u.id, role: 0 }); }}
                      disabled={isPending}
                      className="text-xs px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                    >
                      {isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck className="w-3 h-3" />
                          Make Admin
                        </>
                      )}
                    </button>
                  )}
                  {!isAdmin && currentUser?.userId !== u.id && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(u); }}
                      title="Delete user"
                      className="text-xs p-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Result count */}
      {!isLoading && filteredUsers.length > 0 && (filter !== 'All' || search) && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
          Showing {filteredUsers.length} of {users.length} users
        </p>
      )}
      {deleteTarget && (
        <DeleteUserModal
          userId={deleteTarget.id}
          email={deleteTarget.email}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => {
            setDeleteTarget(null);
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
            showToast({ message: 'User deleted', type: 'success' });
          }}
        />
      )}
    </div>
  );
}

function Skeleton({ count }: { count: number }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="px-5 py-4 animate-pulse flex items-center gap-3">
          <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}
